// src/pages/HomePage.js
import React from 'react';
import '../styles/HomePage.css';
import { motion } from 'framer-motion';

const HomePage = () => {
  return (
    <main className="homepage-container" role="main">
      {/* HERO */}
      <section className="hero-wrapper" aria-label="Hero">
        <motion.div
          className="hero-left"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <h1 className="hero-title">
            Welcome to <strong>Clairifi</strong> — your <strong>AI CFO</strong> for clarity and growth.
          </h1>


          <p className="hero-subtext">
            We’re in early access and it’s free to use while we refine Clairifi with our first users.
          </p>


          {/* CTAs: navy, large tap targets */}
          <div className="hero-ctas" role="group" aria-label="Primary actions">
            <a href="/register" className="btn-navy" aria-label="Try Clairifi now (early access)">
              Try Now
            </a>

            <a href="/dashboard?demo=1" className="btn-navy btn-ghost" aria-label="Open live demo on dashboard">
              Try the live demo
            </a>
          </div>
        </motion.div>

        {/* Dashboard image (stacks 3rd on mobile via CSS order) */}
        <motion.div
          className="hero-right"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <img
            src="/dashboard-preview.png"
            srcSet="/dashboard-preview.png 1x, /dashboard-preview@2x.png 2x"
            alt="Clairifi dashboard preview showing KPIs, charts, and AI summary"
            className="hero-image"
            sizes="(min-width:1280px) 820px, (min-width:768px) 60vw, 90vw"
            style={{ width: '100%', height: 'auto', maxWidth: '820px', objectFit: 'contain' }}
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
        </motion.div>
      </section>

      {/* TRUST BAR (unchanged on desktop, tidy on mobile) */}
      <motion.section
        className="trust-bar"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-20% 0px' }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        Trusted by startups, investors, and CFOs who demand clarity.
      </motion.section>

      {/* WHAT MAKES CLAIRIFI DIFFERENT (kept normal on desktop) */}
      <motion.section
        className="about-section"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <h2>What makes Clairifi different?</h2>
        <div className="features">
          <motion.div
            className="feature"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.03 }}
          >
            <h3>Instant clarity</h3>
            <p>Upload your CSV and get runway, burn rate, and forecasts in seconds.</p>
          </motion.div>
          <motion.div
            className="feature"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.03 }}
          >
            <h3>Predictive AI</h3>
            <p>Forward-looking insights that help you plan better and spend wisely.</p>
          </motion.div>
          <motion.div
            className="feature"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.03 }}
          >
            <h3>CFO-Level Strategy</h3>
            <p>Tailored strategic advice without hiring a finance team.</p>
          </motion.div>
        </div>
      </motion.section>

      {/* HOW CLAIRIFI WORKS — circles that fade in on scroll (mobile only styling) */}
      <section className="how-it-works-diagonal" aria-label="How Clairifi Works">
        <h2 className="section-title">How Clairifi Works</h2>

        <div className="diagonal-flow-wrapper">
          <motion.div
            className="diagonal-step"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45 }}
          >
            <img src="/upload-icon.png" alt="Upload icon" className="step-icon-lg" />
            <div>
              <h3>1. Upload</h3>
              <p>Drag & drop your financial report or transaction statement CSV — no formatting needed.</p>
            </div>
          </motion.div>

          <motion.div
            className="diagonal-step"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, delay: 0.12 }}
          >
            <img src="/ai-icon.png" alt="AI icon" className="step-icon-lg" />
            <div>
              <h3>2. AI Classification</h3>
              <p>Clairifi instantly understands revenue, expenses, burn, and runway.</p>
            </div>
          </motion.div>

          <motion.div
            className="diagonal-step"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, delay: 0.22 }}
          >
            <img src="/forecast-icon.png" alt="Forecast icon" className="step-icon-lg" />
            <div>
              <h3>3. Forecast & Strategy</h3>
              <p>Instant CFO-level insights and strategic recommendations.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CLAIRIFI IS BUILT FOR YOU IF … (rectangles; stack on mobile) */}
      <section className="built-for-enhanced" aria-label="Who it’s for">
        <h2 className="section-title">Clairifi is built for you if…</h2>
        <motion.ul
          className="checklist-enhanced"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <li><span>✓</span> You’re a founder without a finance team</li>
          <li><span>✓</span> You want burn, runway, and spikes auto-calculated</li>
          <li><span>✓</span> You’re preparing for your next raise or board</li>
          <li><span>✓</span> You want clarity in seconds, not days of spreadsheets</li>
        </motion.ul>
      </section>

      {/* FOOTER */}
      <motion.footer
        className="homepage-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="footer-left">
          <p>Let’s talk</p>
          <a href="mailto:contact@getclairifi.com">contact@getclairifi.com</a>
        </div>
        <div className="footer-center">
          <p>Request a Demo</p>
          <p>See real forecasts, strategies, and investor-ready insights</p>
        </div>
        <div className="footer-right">
          <p>Clairifi 2025</p>
          <p>All rights reserved</p>
        </div>
      </motion.footer>
    </main>
  );
};

export default HomePage;
